import { TenantLink, TenantLinkProps } from "components/UI/TenantLink/TenantLink.tsx";
import { useDrawer } from "hooks/useDrawer.ts";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import React, { MouseEvent } from "react";
import { CommissionType, PermissionEnum } from "types/enums.ts";
import { DrawerId } from "types/models.ts";

import { EmptyCell } from "../EmptyCell/EmptyCell.tsx";

interface EntityMap {
    offer: App.Data.OfferData | App.Data.OfferDataOptional;
    advertiser: App.Data.UserData;
    owner: App.Data.UserData;
    integration: App.Data.IntegrationData;
    cascade: App.Data.CascadeData | App.Data.CascadeDataOptional;
    incomingPostback: App.Data.IncomingPostbackData | App.Data.IncomingPostbackDataOptional;
    outgoingPostback: App.Data.OutgoingPostbackData | App.Data.OutgoingPostbackDataOptional;
    formStructure: App.Data.FormStructureData;
    scenario: Paldock.LaravelConnectionCreator.Data.ScenarioData;
    ownerReport: App.Data.UserData;
    advertiserReport: App.Data.UserData;
    offerReport: App.Data.OfferData | App.Data.OfferDataOptional;
    transaction: App.Data.PayoutData;
}

type EntityType = keyof EntityMap;
type LinkProps = TenantLinkProps;
type LinkCoreProps = Omit<TenantLinkProps, "children">;

type OpenMode = "navigate" | "drawer";
type OfferTarget = "default" | "auto" | "editGeneral" | "previewLink";

const defaultEntityPropsLookup: { [K in EntityType]: (entity: EntityMap[K]) => LinkProps | null } = {
    offer: (entity) => ({
        children: entity.title,
        to: "/offer/detail/$offerId",
        params: { offerId: entity.id },
    }),
    advertiser: (entity) => ({
        children: entity.nickname,
        to: "/advertiser",
        search: (prev) => ({
            ...prev,
            drawerIds: DrawerId.AdvertiserDetail,
            "advertiserDetail-advertiserId": entity.id,
        }),
    }),
    owner: (entity) => ({
        children: entity.nickname,
        to: "/affiliate",
        search: (prev) => ({
            ...prev,
            drawerIds: DrawerId.AffiliateDetail,
            "affiliateDetail-affiliateId": entity.id,
        }),
    }),
    integration: (entity) => ({
        children: entity.name,
        to: "/tool/scenario/integration/$integrationId/general",
        params: { integrationId: entity.id ?? "" },
    }),
    cascade: (entity) => {
        if (!entity.offer_id) return null;
        return {
            children: entity.title,
            to: "/offer/edit/$cascadeId/pingtree",
            params: { cascadeId: entity.offer_id },
        };
    },
    incomingPostback: (entity) => ({
        children: entity.name,
        to: "/tool/tracking/s2s-postback/$incomingPostbackId/general",
        params: { incomingPostbackId: entity.id },
    }),
    outgoingPostback: (entity) => ({
        children: entity.name,
        to: "/tool/tracking/tracking-api/$outgoingPostbackId/general",
        params: { outgoingPostbackId: entity.id },
    }),
    formStructure: (entity) => ({
        children: entity.name,
        to: "/tool/structure/lead/$formStructureId/general",
        params: { formStructureId: entity.id ?? "" },
    }),
    scenario: (entity): LinkProps => {
        if (entity.category === "integration") {
            return {
                children: entity.title,
                to: "/tool/scenario/integration/$integrationId/general",
                params: { integrationId: entity.id ?? "" },
            };
        }
        return {
            children: entity.title,
            to: "/tool/validation/$scenarioId/general",
            params: { scenarioId: entity.id ?? "" },
        };
    },
    ownerReport: (entity) => ({
        children: entity.nickname,
        to: "/report/affiliate/$userId",
        params: { userId: entity.id },
    }),
    advertiserReport: (entity) => ({
        children: entity.nickname,
        to: "/report/advertiser/$userId",
        params: { userId: entity.id },
    }),
    offerReport: (entity) => ({
        children: entity.title,
        to: "/report/offer/$offerId",
        params: { offerId: entity.id },
    }),
    transaction: (entity) => ({
        children: entity.id,
        to: "/report/transaction",
        search: (prev) => ({
            ...prev,
            drawerIds: DrawerId.TransactionReportEdit,
            [`${DrawerId.TransactionReportEdit}-id`]: entity.id,
        }),
    }),
};

interface EntityLinkCellProps<K extends EntityType> extends Partial<LinkProps> {
    entity: EntityMap[K] | undefined | null;
    fallbackCellValue?: React.ReactNode;
    mode?: OpenMode;
    offerTarget?: OfferTarget;
}

function entityLinkCellComponentFactory<K extends EntityType>(entityType: K): React.FC<EntityLinkCellProps<K>> {
    return (props: EntityLinkCellProps<K>) => {
        const {
            entity,
            fallbackCellValue = <EmptyCell />,
            children: customChildren,
            mode = "navigate",
            offerTarget = "default",
            ...restProps
        } = props;

        const userUtils = useUser();
        const drawer = useDrawer();
        const { hasPermission } = usePermission();

        if (!entity) return <>{fallbackCellValue}</>;

        const base = defaultEntityPropsLookup[entityType](entity as any);
        if (!base) return <>{fallbackCellValue}</>;
        const { children: defaultChildren, ...restDefaultProps } = base;
        const linkChildren = customChildren ?? defaultChildren;

        // Check permissions for affiliate and advertiser detail links - show text without link if no permission
        if (entityType === "owner" && !hasPermission(PermissionEnum.AffiliateDetailLinkView)) {
            return <span className="text-black">{linkChildren}</span>;
        }

        if (entityType === "advertiser" && !hasPermission(PermissionEnum.AdvertiserDetailLinkView)) {
            return <span className="text-black">{linkChildren}</span>;
        }

        if (mode === "drawer" && entityType === "owner") {
            const own = entity as EntityMap["owner"];
            const open = () => {
                drawer.show(DrawerId.AffiliateDetail, {
                    [`${DrawerId.AffiliateDetail}-affiliateId`]: own.id,
                });
            };
            return (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        open();
                    }}
                    className="underline text-blue-600 hover:underline focus-visible:underline focus-visible:outline-none bg-transparent p-0 text-left truncate"
                    title={own.nickname ?? own.email ?? undefined}
                >
                    {linkChildren}
                </button>
            );
        }

        let effectiveCoreProps: LinkCoreProps = restDefaultProps;

        if (entityType === "offer") {
            const offer = entity as EntityMap["offer"];
            const resolved: OfferTarget =
                offerTarget === "auto" ? (userUtils.isAdmin() ? "editGeneral" : "previewLink") : offerTarget;

            if (resolved === "editGeneral") {
                effectiveCoreProps = {
                    to: "/offer/edit/$offerId/general",
                    params: { offerId: offer.id },
                };
            } else if (resolved === "previewLink") {
                effectiveCoreProps = {
                    to: "/offer/detail/$offerId/link",
                    params: { offerId: offer.id },
                };
            }
        }

        const buildUrl = () => {
            const tenantSlug = userUtils.getActiveTenant()?.slug_name;
            if (!tenantSlug) return null;
            let url = `${window.location.origin}/${tenantSlug}${effectiveCoreProps.to}`;
            if (effectiveCoreProps.params) {
                Object.entries(effectiveCoreProps.params).forEach(([key, value]) => {
                    url = url.replace(`$${key}`, String(value));
                });
            }
            if (effectiveCoreProps.search && typeof effectiveCoreProps.search === "function") {
                const searchParams = new URLSearchParams();
                const searchResult = effectiveCoreProps.search({});
                Object.entries(searchResult).forEach(([key, value]) => {
                    searchParams.set(key, String(value));
                });
                url += `?${searchParams.toString()}`;
            }
            return url;
        };

        const handleMouseDown = (e: MouseEvent<HTMLAnchorElement>) => {
            if (e.button === 1) {
                e.preventDefault();
                e.stopPropagation();
                const url = buildUrl();
                if (url) window.open(url, "_blank", "noopener,noreferrer");
            }
        };

        const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
            e.stopPropagation();
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const url = buildUrl();
                if (url) window.open(url, "_blank", "noopener,noreferrer");
            }
        };

        return (
            <TenantLink
                onMouseDown={handleMouseDown}
                onClick={handleClick}
                className="underline text-blue-600"
                {...effectiveCoreProps}
                {...restProps}
            >
                {linkChildren}
            </TenantLink>
        );
    };
}

interface CommissionLinkCellProps {
    entity: App.Data.CommissionData | App.Data.CommissionDataOptional | undefined | null;
    children?: React.ReactNode;
}

const CommissionLinkCell: React.FC<CommissionLinkCellProps> = ({ entity, children }) => {
    if (!entity) return <EmptyCell />;

    const label = children ?? entity.description ?? entity.id;

    if (!entity.commission_type) {
        return <span className="text-black">{label}</span>;
    }

    const isTenant = entity.commission_type === CommissionType.Tenant;

    const to = isTenant ? "/advertiser/commission" : "/affiliate/commission/$commissionGroupId";

    const params = isTenant ? undefined : { commissionGroupId: entity.commission_group_id ?? "user-group" };

    return (
        <TenantLink
            to={to}
            params={params}
            search={(prev) => ({
                ...prev,
                drawerIds: DrawerId.EditCommission,
                [`${DrawerId.EditCommission}-id`]: entity.id,
            })}
            onClick={(e) => e.stopPropagation()}
            className="underline text-blue-600"
        >
            {label}
        </TenantLink>
    );
};

export const EntityLinkCell = {
    Offer: entityLinkCellComponentFactory("offer"),
    Advertiser: entityLinkCellComponentFactory("advertiser"),
    Owner: entityLinkCellComponentFactory("owner"),
    Integration: entityLinkCellComponentFactory("integration"),
    Cascade: entityLinkCellComponentFactory("cascade"),
    IncomingPostback: entityLinkCellComponentFactory("incomingPostback"),
    OutgoingPostback: entityLinkCellComponentFactory("outgoingPostback"),
    FormStructure: entityLinkCellComponentFactory("formStructure"),
    Scenario: entityLinkCellComponentFactory("scenario"),
    OwnerReport: entityLinkCellComponentFactory("ownerReport"),
    AdvertiserReport: entityLinkCellComponentFactory("advertiserReport"),
    OfferReport: entityLinkCellComponentFactory("offerReport"),
    Commission: CommissionLinkCell,
    Transaction: entityLinkCellComponentFactory("transaction"),
};
